import React, { useMemo } from "react"
import { formatCurrency } from "@ncs/ts-utils"
import { Box } from "../layout"
import { Paragraph } from "./Paragraph"
import { css, Theme } from "@emotion/react"
import { calculateEcommDiscount } from "../../util"

export interface PartClearancePriceEcommProps {
	price: number | string | null
	netPrice: number | string | null
	clearanceDiscount?: number | null
	isParent?: boolean | null
	quantity?: number | null
	titleSize?: "big" | "small" | null
	showClearanceLegend?: boolean | null
}

export const PartClearancePriceEcomm: React.FC<PartClearancePriceEcommProps> = ({
	price,
	netPrice,
	isParent = false,
	quantity,
	titleSize = "small",
	showClearanceLegend = false,
	clearanceDiscount,
}) => {
	const parsedPrice = parseFloat(price?.toString() || "0")
	const parsedNetPrice = parseFloat(netPrice?.toString() || "0")
	const hasClearance = parsedPrice !== 0 && parsedNetPrice !== 0
	const title = titleSize === "big" ? "1.8" : "1.25"

	const discount = useMemo(
		() => calculateEcommDiscount(parsedPrice, parsedNetPrice),
		[parsedPrice, parsedNetPrice]
	)

	const renderPriceContent = () => {
		if (quantity) {
			return parsedNetPrice < parsedPrice ?
					<div css={priceBoxQuantity}>
						<div>
							<Paragraph fontSize={title} customColor="#dc2626" bold>
								{formatCurrency(parsedNetPrice * quantity)}
							</Paragraph>
							<Paragraph css={strikedPrice(title)}>
								{formatCurrency(parsedPrice * quantity)}
							</Paragraph>
						</div>
						{quantity > 1 && (
							<Paragraph small css={eachPrice}>
								{formatCurrency(parsedNetPrice)} each
							</Paragraph>
						)}
					</div>
				:	<div css={priceBoxQuantity}>
						<Paragraph fontSize={title} customColor="#111827" bold>
							{formatCurrency(parsedPrice * quantity)}
						</Paragraph>
						{quantity > 1 && (
							<Paragraph small css={eachPrice}>
								{formatCurrency(parsedNetPrice)} each
							</Paragraph>
						)}
					</div>
		} else {
			return parsedNetPrice < parsedPrice ?
					<div css={priceBox}>
						<Paragraph
							fontSize={title}
							customColor={isParent ? "black" : "#dc2626"}
							bold
						>
							{formatCurrency(parsedNetPrice)}
							{isParent ? " +" : null}
						</Paragraph>
					</div>
				:	<div css={priceBox}>
						<Paragraph fontSize={title} customColor="#dc2626" bold>
							{formatCurrency(isParent ? parsedNetPrice : parsedPrice)}
							{isParent ? " +" : null}
						</Paragraph>
						{(quantity ?? 1) > 1 && (
							<Paragraph small css={eachPrice}>
								{formatCurrency(parsedNetPrice)} each
							</Paragraph>
						)}
					</div>
		}
	}

	return (
		<Box className="partClearancePriceEcomm">
			<>
				{hasClearance && renderPriceContent()}
				{showClearanceLegend && (
					<Paragraph small customColor="#374151">
						Clearance ({isParent ? clearanceDiscount : discount}% Off)
					</Paragraph>
				)}
			</>
		</Box>
	)
}

const priceBox = css`
	display: flex;
	flex-direction: column;
`

const eachPrice = css`
	color: #4b5563;
	font-weight: 400;
`

const priceBoxQuantity = (theme: Theme) => css`
	display: flex;
	flex-direction: column;

	> div {
		display: flex;
		${theme.breakpoints.down("xs")} {
			flex-direction: column;
		}
	}
`

const strikedPrice = (title: string) => (theme: Theme) => css`
	color: #111827;
	font-size: ${title}rem;
	font-weight: 400;
	text-decoration: line-through;
	margin-left: 1.5rem;

	${theme.breakpoints.down("xs")} {
		margin-left: 0;
	}
`
